import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import './CTCCalculator.css';
import tektapLogo from '../../assets/images/tektap2.png';
import signatureImage from '../../assets/images/PoliSign.png';
import { addRobotoFont } from '../../utils/pdfFonts';

// Icons
const CalculatorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="8" y2="10.01" />
    <line x1="12" y1="10" x2="12" y2="10.01" />
    <line x1="16" y1="10" x2="16" y2="10.01" />
    <line x1="8" y1="14" x2="8" y2="14.01" />
    <line x1="12" y1="14" x2="12" y2="14.01" />
    <line x1="16" y1="14" x2="16" y2="14.01" />
    <line x1="8" y1="18" x2="8" y2="18.01" />
    <line x1="12" y1="18" x2="12" y2="18.01" />
    <line x1="16" y1="18" x2="16" y2="18.01" />
  </svg>
);

const RupeeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 3h12M6 8h12M6 13l8.5 8M6 13h3c3.5 0 5-2 5-5s-1.5-5-5-5" />
  </svg>
);

const ResetIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CTCCalculator = () => {
  const [annualCTC, setAnnualCTC] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerFormData, setOfferFormData] = useState({
    fullName: '',
    emailId: '',
    letterDate: new Date().toISOString().split('T')[0],
    dateOfJoining: '',
    designation: ''
  });

  // Salary component percentages (can be customized)
  const components = {
    basic: 0.50,           // 50% of CTC
    conveyance: 0.05,      // 5% of CTC
  };

  useEffect(() => {
    if (annualCTC && !isNaN(annualCTC) && Number(annualCTC) > 0) {
      calculateBreakdown(Number(annualCTC));
    } else {
      setBreakdown(null);
    }
  }, [annualCTC]);

  const calculateBreakdown = (ctc) => {
    const basic = ctc * components.basic;
    const hra = basic * 0.4; // HRA is 40% of Basic
    const conveyance = Math.min(ctc * components.conveyance, 19200); // Capped at 1600/month
    
    // Employee PF contribution
    const employeePF = Math.min(basic * 0.12, 21600);
    
    // Employer contributions
    const employerPF = Math.min(basic * 0.12, 21600); // 12% of Basic, max 1800/month
    const gratuity = basic * 0.0481; // 4.81% of Basic Salary
    const medicalInsurance = 800 * 12; // Fixed Medical Insurance Premium - 800/month
    
    // Special Allowance = CTC - (sum of all other components)
    const specialAllowance = ctc - basic - hra - conveyance - employeePF - employerPF - gratuity - medicalInsurance;
    
    // Professional Tax - Fixed 200/month
    const professionalTax = 200 * 12;
    
    // Fixed Salary
    const grossSalary = basic + hra + specialAllowance + conveyance;
    
    // Gross Salary (Fixed Salary + Employee PF)
    const totalGross = grossSalary + employeePF;
    
    // Net Salary (Gross - Employee Deductions)
    const totalDeductions = employeePF + professionalTax;
    const netSalary = grossSalary - totalDeductions;
    
    // In-hand salary (monthly) = Fixed Salary - Professional Tax
    const monthlyInHand = (grossSalary / 12) - 200;

    setBreakdown({
      annual: {
        basic: Math.round(basic),
        hra: Math.round(hra),
        specialAllowance: Math.round(specialAllowance),
        conveyance: Math.round(conveyance),
        grossSalary: Math.round(grossSalary),
        totalGross: Math.round(totalGross),
        employerPF: Math.round(employerPF),
        gratuity: Math.round(gratuity),
        medicalInsurance: Math.round(medicalInsurance),
        totalBenefits: Math.round(employerPF + gratuity + medicalInsurance),
        employeePF: Math.round(employeePF),
        professionalTax: Math.round(professionalTax),
        totalDeductions: Math.round(totalDeductions),
        netSalary: Math.round(netSalary),
      },
      monthly: {
        basic: Math.round(basic / 12),
        hra: Math.round(hra / 12),
        specialAllowance: Math.round(specialAllowance / 12),
        conveyance: Math.round(conveyance / 12),
        grossSalary: Math.round(grossSalary / 12),
        totalGross: Math.round(totalGross / 12),
        employerPF: Math.round(employerPF / 12),
        gratuity: Math.round(gratuity / 12),
        medicalInsurance: Math.round(medicalInsurance / 12),
        totalBenefits: Math.round((employerPF + gratuity + medicalInsurance) / 12),
        employeePF: Math.round(employeePF / 12),
        professionalTax: Math.round(professionalTax / 12),
        totalDeductions: Math.round(totalDeductions / 12),
        netSalary: Math.round(netSalary / 12),
        inHand: Math.round(monthlyInHand),
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleReset = () => {
    setAnnualCTC('');
    setBreakdown(null);
  };

  const handleCTCChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAnnualCTC(value);
  };

  const handleOfferFormChange = (e) => {
    const { name, value } = e.target;
    setOfferFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateOfferLetter = () => {
    setShowOfferModal(true);
  };

  const handleCloseOfferModal = () => {
    setShowOfferModal(false);
  };

  const handleSubmitOfferForm = (e) => {
    e.preventDefault();
    generateOfferLetterPDF();
    setShowOfferModal(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const numberToWords = (num) => {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
      'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    
    if (num === 0) return 'zero';
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    if (num >= 10000000) {
      const crores = Math.floor(num / 10000000);
      const remainder = num % 10000000;
      return convertLessThanThousand(crores) + ' crore' + (remainder ? ' ' + numberToWords(remainder) : '');
    }
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      const remainder = num % 100000;
      return convertLessThanThousand(lakhs) + ' lakh' + (lakhs > 1 ? 's' : '') + (remainder ? ' ' + numberToWords(remainder) : '');
    }
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      return convertLessThanThousand(thousands) + ' thousand' + (remainder ? ' ' + convertLessThanThousand(remainder) : '');
    }
    return convertLessThanThousand(num);
  };

  const generateOfferLetterPDF = async () => {
    if (!breakdown || !offerFormData.fullName) return;

    // Use imported PNG logo directly
    const logoPngDataUrl = tektapLogo;

    const doc = new jsPDF();
    
    // Load Roboto font
    const fontLoaded = await addRobotoFont(doc);
    const fontFamily = fontLoaded ? 'Roboto' : 'helvetica';
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20; // Left margin
    const rightMargin = 20; // Right margin
    const contentWidth = pageWidth - margin - rightMargin; // Proper content width
    const lineHeight = 6; // Standard line height for 11pt font
    
    // Colors
    const darkText = [0, 0, 0];
    const blueLink = [0, 102, 204];
    
    const firstName = offerFormData.fullName.split(' ')[0];
    const ctcAmount = Number(annualCTC);
    const ctcInWords = numberToWords(ctcAmount).charAt(0).toUpperCase() + numberToWords(ctcAmount).slice(1);
    
    // Helper function to add logo (using tektap2.png)
    // Returns the y position after the logo for proper content positioning
    const addLogo = (startY) => {
      const logoWidth = 30; // Width in mm (reduced from 40)
      const logoHeight = 16; // Height in mm (maintains aspect ratio)
      const logoX = (pageWidth - logoWidth) / 2;
      
      if (logoPngDataUrl) {
        // Add TekTap PNG logo image centered on the page
        doc.addImage(logoPngDataUrl, 'PNG', logoX, startY, logoWidth, logoHeight);
      } else {
        // Fallback: Draw logo using text
        doc.setFontSize(18);
        doc.setFont(fontFamily, 'bold');
        doc.setTextColor(40, 70, 148);
        doc.text('Tek', logoX + 3, startY + 10);
        doc.setTextColor(239, 127, 26);
        doc.text('Tap', logoX + 18, startY + 10);
      }
      return startY + logoHeight + lineHeight; // Return position after logo with one line space
    };
    
    // Helper function to add footer
    const addFooter = () => {
      const footerStartY = pageHeight - 28;
      
      // Draw separator line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, footerStartY, pageWidth - margin, footerStartY);
      
      // Company name - bold and slightly larger
      doc.setFontSize(8);
      doc.setFont(fontFamily, 'bold');
      doc.setTextColor(40, 70, 148); // TekTap blue color
      doc.text('TekTap Consulting Private Limited', pageWidth / 2, footerStartY + 4, { align: 'center' });
      
      // Address lines - normal weight, gray color
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.text('Corp. Office: #1004, 10th Floor, Jain Sadguru Capital Park, Madhapur, Hyderabad - 500081', pageWidth / 2, footerStartY + 8, { align: 'center' });
      doc.text('Regd. Office: #407, Swastik Residency, Bagh Ameer, Kukatpally, Hyderabad - 500072', pageWidth / 2, footerStartY + 12, { align: 'center' });
      
      // CIN and Website - single centered line
      doc.setFontSize(7);
      doc.setFont(fontFamily, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('CIN: U74999TG2019PTC136164   |   www.tektap.in', pageWidth / 2, footerStartY + 16, { align: 'center' });
    };
    
    // Helper function to add signature
    const addSignature = (yPos) => {
      const signatureX = pageWidth - rightMargin;
      const footerStartY = pageHeight - 28;
      const signatureHeight = 25; // Total height needed for signature block
      
      // Check if signature fits above footer, if not adjust yPos
      if (yPos + signatureHeight > footerStartY - 5) {
        yPos = footerStartY - signatureHeight - 5;
      }
      
      // "Sincerely," - elegant styling
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...darkText);
      doc.text('Sincerely,', signatureX, yPos, { align: 'right' });
      
      // Add signature image - compact size
      const sigImgWidth = 22;
      const sigImgHeight = 8;
      doc.addImage(signatureImage, 'PNG', signatureX - sigImgWidth, yPos + 2, sigImgWidth, sigImgHeight);
      
      // Name - bold
      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(10);
      doc.text('Poli Paul', signatureX, yPos + 13, { align: 'right' });
      
      // Title - normal, slightly smaller
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Manager – Human Resources', signatureX, yPos + 18, { align: 'right' });
      
      // Company - normal, gray
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text('TekTap Consulting Private Limited', signatureX, yPos + 23, { align: 'right' });
    };

    // ==================== PAGE 1: Offer Letter ====================
    let yPos = addLogo(10); // Start logo at y=10, get position after logo
    
    // "Offer Letter" title
    doc.setFontSize(12);
    doc.setFont(fontFamily, 'normal');
    doc.setTextColor(...darkText);
    doc.text('Offer Letter', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'bold');
    doc.text('TEKTAP Confidential', margin, yPos);
    yPos += 7;
    doc.setFont(fontFamily, 'normal');
    doc.text(formatDate(offerFormData.letterDate), margin, yPos);
    
    yPos += 12;
    doc.text(offerFormData.fullName, margin, yPos);
    yPos += 6;
    doc.setTextColor(...blueLink);
    doc.text(offerFormData.emailId, margin, yPos);
    
    yPos += 12;
    doc.setTextColor(...darkText);
    doc.setFont(fontFamily, 'normal');
    doc.text('Dear ', margin, yPos);
    doc.setFont(fontFamily, 'bold');
    doc.text(firstName + ',', margin + 12, yPos);
    
    yPos += 10;
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(11);
    
    const congrats1 = 'Congratulations!! We are pleased to confirm that you have been selected to work for TekTap Consulting Private Limited.';
    const splitCongrats1 = doc.splitTextToSize(congrats1, contentWidth);
    doc.text(splitCongrats1, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitCongrats1.length * lineHeight + 4;
    
    const congrats2 = `Congratulations! We are very excited that you will be joining TekTap Consulting Private Limited as a ${offerFormData.designation}, effective from Tentatively ${formatDateShort(offerFormData.dateOfJoining)}. This letter will confirm the terms of your employment.`;
    const splitCongrats2 = doc.splitTextToSize(congrats2, contentWidth);
    doc.text(splitCongrats2, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitCongrats2.length * lineHeight + 8;
    
    // Position and Duties
    doc.setFont(fontFamily, 'bold');
    doc.text('Position and Duties', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const positionText = `You shall serve in the ${offerFormData.designation} position of the Company and shall perform all the duties of that position. You will be in the probationary period for three months from the date of joining. Your position, job description, obligations, and responsibilities may be modified from time to time at the Company's sole discretion.`;
    const splitPosition = doc.splitTextToSize(positionText, contentWidth);
    doc.text(splitPosition, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitPosition.length * lineHeight + 8;
    
    // Compensation
    doc.setFont(fontFamily, 'bold');
    doc.text('Compensation', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const compText = `Your annual compensation CTC will be ${ctcAmount.toLocaleString('en-IN')}.00 (Rupees ${ctcInWords} only) with all-inclusive, as described in Exhibit A. Please note TDS, Provident Fund, Gratuity and Professional Tax will be deducted as per the prevailing rates. Please see Exhibit A for more details.`;
    const splitComp = doc.splitTextToSize(compText, contentWidth);
    doc.text(splitComp, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitComp.length * lineHeight + 8;
    
    // Medical Insurance
    doc.setFont(fontFamily, 'bold');
    doc.text('Medical Insurance', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const medicalText = "You are eligible for the organization's health insurance policy, and the details will be shared post joining, and it will be applicable after completion of one month in our organization.";
    const splitMedical = doc.splitTextToSize(medicalText, contentWidth);
    doc.text(splitMedical, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    
    addFooter();
    
    // ==================== PAGE 2: Leave Policy, Background, Notice, Working ====================
    doc.addPage();
    yPos = addLogo(10);
    doc.setFontSize(11);
    
    // Leave Policy
    doc.setFont(fontFamily, 'bold');
    doc.setTextColor(...darkText);
    doc.text('Leave Policy', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const leaveText = "You will be eligible for leave in accordance with the rules and policies TekTap Consulting Private Limited as approved and modified from time to time in the sole discretion of TekTap Consulting Private Limited in compliance with applicable law. Leave regulations will be as per the Client's leave policy, and available leave is subject to the Client's approval. Any leave taken beyond the approved leave shall be treated as a loss of pay. Leave policies are as described in Exhibit B.";
    const splitLeave = doc.splitTextToSize(leaveText, contentWidth);
    doc.text(splitLeave, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    
    yPos += splitLeave.length * lineHeight + 10;
    
    // Background check
    doc.setFont(fontFamily, 'bold');
    doc.text('Background check', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const bgText = 'Post joining, your submitted documents will proceed with the background check. If there is any discrepancy in the background check report, your service will be terminated immediately without any prior notice.';
    const splitBg = doc.splitTextToSize(bgText, contentWidth);
    doc.text(splitBg, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitBg.length * lineHeight + 10;
    
    // Notice period
    doc.setFont(fontFamily, 'bold');
    doc.text('Notice period', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const noticeText = "Either party may terminate this agreement at any time upon 60 days written or salary in lieu thereof from the date of joining. However, if the performance or conduct is not in line with the client organization standard, then your service may be terminated immediately without any prior notice. During the probationary period also you are entitled to serve a notice period of 60 days. The Company reserves the unconditional right to change, amend, modify, and rescind this policy at any time.";
    const splitNotice = doc.splitTextToSize(noticeText, contentWidth);
    doc.text(splitNotice, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitNotice.length * lineHeight + 10;
    
    // Working Policy
    doc.setFont(fontFamily, 'bold');
    doc.text('Working Policy', margin, yPos);
    yPos += 7;
    
    doc.setFont(fontFamily, 'normal');
    const workingText1 = "You will adhere to the general rules and regulations, working hours, etc., as per the Client's guidelines. If there is any change in time, location, and plan in the project, the Client shall be informed in advance, and you shall adhere to the same. If you have to travel overseas on a project, then the Company will pay all the travel expenses directly to you as per your eligibility. You are entitled to adhere to the working hours as per the client. If there is any discrepancy in the working hours, you will be paid only for the hours worked.";
    const splitWorking1 = doc.splitTextToSize(workingText1, contentWidth);
    doc.text(splitWorking1, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitWorking1.length * lineHeight + 4;
    
    const workingText2 = 'Submission of Timesheets is mandatory every month. As you are working with a client, you are responsible for submitting the approved timesheet in the format given on or before the last day of every month. The payroll process depends on the duly submission of the approved timesheet from your manager.';
    const splitWorking2 = doc.splitTextToSize(workingText2, contentWidth);
    doc.text(splitWorking2, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitWorking2.length * lineHeight + 8;
    
    addSignature(yPos);
    addFooter();
    
    // ==================== PAGE 3: Exhibit A - Compensation ====================
    doc.addPage();
    yPos = addLogo(10);
    
    // Table variables
    const tableStartX = margin;
    const tableWidth = contentWidth;
    const col1Width = tableWidth * 0.50;
    const col2Width = tableWidth * 0.25;
    const col3Width = tableWidth * 0.25;
    const rowHeight = 9;
    const cardRadius = 3;
    const blueAccent = [43, 75, 153]; // TekTap blue
    const lightBlueBg = [240, 245, 255];
    const grayText = [100, 100, 100];
    
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...darkText);
    doc.text('Exhibit A', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFontSize(11);
    doc.text('Compensation Structure', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    
    // Calculate subtotals
    const fixedSalaryMonthly = breakdown.monthly.basic + breakdown.monthly.hra + breakdown.monthly.specialAllowance + breakdown.monthly.conveyance;
    const fixedSalaryAnnual = breakdown.annual.basic + breakdown.annual.hra + breakdown.annual.specialAllowance + breakdown.annual.conveyance;
    const grossSalaryMonthly = fixedSalaryMonthly + breakdown.monthly.employeePF;
    const grossSalaryAnnual = fixedSalaryAnnual + breakdown.annual.employeePF;
    
    // ==================== EARNINGS SECTION ====================
    // Card background with left blue border
    const earningsCardHeight = rowHeight * 9;
    doc.setFillColor(250, 251, 252);
    doc.roundedRect(tableStartX, yPos, tableWidth, earningsCardHeight, cardRadius, cardRadius, 'F');
    doc.setFillColor(...blueAccent);
    doc.rect(tableStartX, yPos + cardRadius, 3, earningsCardHeight - cardRadius * 2, 'F');
    
    // Earnings Header
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkText);
    doc.text('Earnings', tableStartX + 10, yPos + 7);
    yPos += rowHeight + 3;
    
    // Column Headers
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.setFont(fontFamily, 'bold');
    doc.text('COMPONENT', tableStartX + 10, yPos + 5);
    doc.text('MONTHLY', tableStartX + col1Width + col2Width - 10, yPos + 5, { align: 'right' });
    doc.text('ANNUAL', tableStartX + tableWidth - 10, yPos + 5, { align: 'right' });
    yPos += rowHeight;
    
    // Earnings rows
    const earningsData = [
      { component: 'Basic Salary', monthly: breakdown.monthly.basic, annual: breakdown.annual.basic },
      { component: 'House Rent Allowance (HRA)', monthly: breakdown.monthly.hra, annual: breakdown.annual.hra },
      { component: 'Special Allowance', monthly: breakdown.monthly.specialAllowance, annual: breakdown.annual.specialAllowance },
      { component: 'Conveyance Allowance', monthly: breakdown.monthly.conveyance, annual: breakdown.annual.conveyance },
    ];
    
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    
    earningsData.forEach((row) => {
      doc.text(row.component, tableStartX + 10, yPos + 6);
      doc.text('₹' + row.monthly.toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
      doc.text('₹' + row.annual.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
      yPos += rowHeight;
    });
    
    // Fixed Salary subtotal row (light blue background)
    doc.setFillColor(...lightBlueBg);
    doc.rect(tableStartX + 3, yPos, tableWidth - 3, rowHeight, 'F');
    doc.setFont(fontFamily, 'bold');
    doc.setTextColor(...blueAccent);
    doc.text('Fixed Salary', tableStartX + 10, yPos + 6);
    doc.text('₹' + fixedSalaryMonthly.toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
    doc.text('₹' + fixedSalaryAnnual.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
    yPos += rowHeight;
    
    // Employee PF Contribution row
    doc.setFont(fontFamily, 'normal');
    doc.setTextColor(...darkText);
    doc.text('Employee PF Contribution', tableStartX + 10, yPos + 6);
    doc.text('₹' + breakdown.monthly.employeePF.toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
    doc.text('₹' + breakdown.annual.employeePF.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
    yPos += rowHeight;
    
    // Gross Salary subtotal row (light blue background)
    doc.setFillColor(...lightBlueBg);
    doc.rect(tableStartX + 3, yPos, tableWidth - 3, rowHeight, 'F');
    doc.setFont(fontFamily, 'bold');
    doc.setTextColor(...blueAccent);
    doc.text('Gross Salary', tableStartX + 10, yPos + 6);
    doc.text('₹' + grossSalaryMonthly.toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
    doc.text('₹' + grossSalaryAnnual.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
    yPos += rowHeight + 15;
    
    // ==================== BENEFITS SECTION ====================
    const benefitsCardHeight = rowHeight * 6;
    doc.setFillColor(250, 251, 252);
    doc.roundedRect(tableStartX, yPos, tableWidth, benefitsCardHeight, cardRadius, cardRadius, 'F');
    doc.setFillColor(...blueAccent);
    doc.rect(tableStartX, yPos + cardRadius, 3, benefitsCardHeight - cardRadius * 2, 'F');
    
    // Benefits Header
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkText);
    doc.text('Benefits', tableStartX + 10, yPos + 7);
    yPos += rowHeight + 3;
    
    // Column Headers
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.setFont(fontFamily, 'bold');
    doc.text('COMPONENT', tableStartX + 10, yPos + 5);
    doc.text('MONTHLY', tableStartX + col1Width + col2Width - 10, yPos + 5, { align: 'right' });
    doc.text('ANNUAL', tableStartX + tableWidth - 10, yPos + 5, { align: 'right' });
    yPos += rowHeight;
    
    // Benefits rows
    const benefitsTableData = [
      { component: 'Employer PF Contribution', monthly: breakdown.monthly.employerPF, annual: breakdown.annual.employerPF },
      { component: 'Gratuity', monthly: breakdown.monthly.gratuity, annual: breakdown.annual.gratuity },
      { component: 'Medical Insurance Premium', monthly: breakdown.monthly.medicalInsurance, annual: breakdown.annual.medicalInsurance },
    ];
    
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    
    benefitsTableData.forEach((row) => {
      doc.text(row.component, tableStartX + 10, yPos + 6);
      doc.text('₹' + row.monthly.toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
      doc.text('₹' + row.annual.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
      yPos += rowHeight;
    });
    
    // Total CTC row (light blue background)
    doc.setFillColor(...lightBlueBg);
    doc.rect(tableStartX + 3, yPos, tableWidth - 3, rowHeight, 'F');
    doc.setFont(fontFamily, 'bold');
    doc.setTextColor(...blueAccent);
    doc.text('Total CTC', tableStartX + 10, yPos + 6);
    doc.text('₹' + Math.round(ctcAmount / 12).toLocaleString('en-IN'), tableStartX + col1Width + col2Width - 10, yPos + 6, { align: 'right' });
    doc.text('₹' + ctcAmount.toLocaleString('en-IN'), tableStartX + tableWidth - 10, yPos + 6, { align: 'right' });
    yPos += rowHeight + 12;
    
    // Notes - positioned on the left side
    const notesStartY = yPos;
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    doc.text('Note:', margin, yPos);
    yPos += 5;
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(9);
    const noteText = 'Please note that the Provident Fund, Gratuity, Professional Tax and TDS deductions are as per prevailing rates.';
    const noteMaxWidth = contentWidth * 0.55; // Notes take left 55% of width
    const splitNote = doc.splitTextToSize(noteText, noteMaxWidth);
    doc.text(splitNote, margin, yPos);
    yPos += splitNote.length * 4 + 4;
    
    const salaryNote = 'The Salary gets credited on or before 5th of the month.';
    doc.text(salaryNote, margin, yPos);
    
    // Signature positioned on the right side, aligned with notes
    addSignature(notesStartY);
    addFooter();
    
    // ==================== PAGE 4: Exhibit B - Leave Policy ====================
    doc.addPage();
    yPos = addLogo(10);
    
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...darkText);
    doc.text('Exhibit B', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFontSize(12);
    doc.text('Leave Policy', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'normal');
    const leavePolicyIntro = 'To provide details in which TekTap Consulting Private Limited employees are eligible for various leave types, along with the guidelines and procedures for availing leaves.';
    const splitLPIntro = doc.splitTextToSize(leavePolicyIntro, contentWidth);
    doc.text(splitLPIntro, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitLPIntro.length * lineHeight + 6;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Scope', margin, yPos);
    yPos += 8;
    doc.setFont(fontFamily, 'normal');
    const scopeText = "This policy applies to all TekTap Consulting Private Limited regular employees associated with all clients (referred to as the 'Company').";
    const splitScope = doc.splitTextToSize(scopeText, contentWidth);
    doc.text(splitScope, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitScope.length * lineHeight + 6;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Policy', margin, yPos);
    yPos += 8;
    
    doc.text('Type 1:', margin, yPos);
    yPos += 6;
    doc.text('Casual leave/privileged leave:', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const casualLeaveText = 'You are entitled to avail of 12 working days of Casual leave per year (Jan to Dec). Leave will be credited every month, starting with the date of commencement of employment. The casual leave will be credited on a pro-rata basis based on the date of joining. The casual leave will be credited one day per month. However, the employee can carry forward the leave and can be accrued in subsequent months subjected to not more than three days of leave per month. If the leave accrued more than the eligible leave of that particular month, the leave would be treated as leave without pay (LWP). Leave will not be carried forward to the following calendar year.';
    const splitCasual = doc.splitTextToSize(casualLeaveText, contentWidth);
    doc.text(splitCasual, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitCasual.length * lineHeight + 4;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Procedure for Casual/Privileged Leave Application:', margin, yPos);
    yPos += 6;
    
    doc.setFont(fontFamily, 'normal');
    const casualProcedure = "Employees are required to apply for casual leave in advance for approval through email. In case of an emergency, employees must contact their manager/supervisor/Client coordinator to relay the emergency. Upon their return to work, they provide sufficient proof of the emergency to obtain approval for the emergency leave request. The supervisor, in exceptional circumstances, can waive off the same. If the employee wishes to extend their leave, they must request writing or informing the associated manager/supervisor/Client coordinator by telephone. The employee will then receive approval or denial of the extension request. Suppose the employee does not receive a reply. In that case, the application for extension of leave will be treated as rejected, and the employee will be required to resume duties on completion of the leave initially granted. Management has the discretion to revise, curtail, or revoke approved leave pending operational requirements. If an employee takes, extends, or remains on leave without approval, this will be considered misconduct and may result in disciplinary action, up to and including termination. If a Company defined Holiday or weekly-offs occur during a period of requested and approved earned leave, these dates will not be considered casual leave. Casual leave is not encashable either during employment or at the time of separation from the Company. However, overutilization of the Casual leave shall be deducted from the final settlement calculation of the employee.";
    const splitCasualProc = doc.splitTextToSize(casualProcedure, contentWidth);
    doc.text(splitCasualProc, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    
    addFooter();
    
    // ==================== PAGE 5: Leave Policy Continued ====================
    doc.addPage();
    yPos = addLogo(10);
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...darkText);
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Type 2: Sick Leave', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const sickLeaveText = "TekTap Consulting Private Limited eligible employees will be eligible for 6-days of sick leave per year (Jan to Dec) & new joiners will be credited leave on a pro-rata basis. The sick leave will be credited 0.5 (Half day) days per month. However, the employee can carry forward the leave and can be accrued in subsequent months subjected to more than three days of leave per month. Company holidays and weekly offs occurring during sick leave will not be included as part of sick leave. Employees must notify Superior/Manager no later than 24 hours. If the leave accrued more than the eligible leave of that particular month, the leave would be treated as leave without pay (LWP). Leave will not be carried forward to the following calendar year. Sick leave is not encashable either during employment or at the time of separation from the Company. However, overutilization of the sick leave shall be deducted from the final settlement calculation of the employee.";
    const splitSick = doc.splitTextToSize(sickLeaveText, contentWidth);
    doc.text(splitSick, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitSick.length * lineHeight + 4;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Type 3: Public Holidays', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const publicHolidayText = 'Eligible employees are entitled to 10 customarily paid holidays identified by the Company at the beginning of each calendar year.';
    const splitPublic = doc.splitTextToSize(publicHolidayText, contentWidth);
    doc.text(splitPublic, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitPublic.length * lineHeight + 10;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Type 4: Leave Without Pay (LWP)', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const unpaidText = 'Employees may apply for leave without pay in case of negative or insufficient or no leave balance, Reporting Manager will send his/her recommendation to the Account Manager of the company for final approval. The holidays/weekly off within the period of LWP will not be paid for and salary is not credited to the employee for the duration of such leave without pay. LWP more than 15 days needs approval from the Head HR.';
    const splitUnpaid = doc.splitTextToSize(unpaidText, contentWidth);
    doc.text(splitUnpaid, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    
    yPos += splitUnpaid.length * lineHeight + 10;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Terms & Conditions', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const termsText = "All applications for leave will be made through the email marking HR in the copy. All taxes arising out of any encashment of leave as provided in this policy will be the employee's responsibility.";
    const splitTermsLP = doc.splitTextToSize(termsText, contentWidth);
    doc.text(splitTermsLP, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitTermsLP.length * lineHeight + 10;
    
    doc.setFont(fontFamily, 'bold');
    doc.text('Disclaimer', margin, yPos);
    yPos += 6;
    
    doc.setFont(fontFamily, 'normal');
    const disclaimerText = 'The Company reserves the unconditional right to change, amend, modify, and rescind this leave policy at any time.';
    const splitDisclaimer = doc.splitTextToSize(disclaimerText, contentWidth);
    doc.text(splitDisclaimer, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitDisclaimer.length * lineHeight + 10;
    
    addSignature(yPos);
    addFooter();
    
    // ==================== PAGE 6: NDA ====================
    doc.addPage();
    yPos = addLogo(10);
    doc.setFontSize(11);
    
    doc.setFont(fontFamily, 'bold');
    doc.setTextColor(...darkText);
    doc.text('Non-disclosure Agreement', margin, yPos);
    yPos += 8;
    
    doc.setFont(fontFamily, 'normal');
    const ndaText = `In this agreement's performance, either party may disclose to the other certain trade secrets and additional information that is of value to its owner and is treated as confidential (collectively, "Confidential Information"). Both the parties acknowledge and agree that the Confidential Information shall remain the disclosing party's sole and exclusive property of a third party providing such information to the disclosing party. The receiving party agrees to hold the Confidential Information disclosed by the other party in strictest confidence and not to, directly or indirectly, copy, use, reproduce, distribute, manufacture, duplicate, reveal, report, publish, disclose, cause to be disclosed, or otherwise transfer the Confidential Information for any purpose whatsoever other than as expressly provided by this agreement. Subject to the terms set forth herein, the receiving party shall not disclose the Confidential Information to a third party without the written consent of the disclosing party and shall protect the Confidential Information of the disclosing party with the same degree of protection and care the receiving party uses to protect its Confidential Information, but in no event less than reasonable care. Regarding confidential information constituting a trade secret, the obligations in this section shall continue for so long as such information constitutes a trade secret under applicable law. With regard to all other Confidential Information, the obligations in this section shall continue for the term of this agreement and five years after that. However, if the Consultant's performance or conduct is not in line with the Client's organization standard, your services may be terminated immediately.`;
    const splitNda = doc.splitTextToSize(ndaText, contentWidth);
    doc.text(splitNda, margin, yPos, { align: 'justify', maxWidth: contentWidth });
    yPos += splitNda.length * lineHeight + 10;
    
    addSignature(yPos);
    addFooter();
    
    // ==================== PAGE 7: Regulations & Policies ====================
    doc.addPage();
    yPos = addLogo(10);
    
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkText);
    doc.text('TekTap Consulting Private Limited Regulations & Policies', margin, yPos);
    
    yPos += 10;
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(10);
    
    // Helper to render paragraph with consistent spacing
    const renderParagraph = (text, startY) => {
      const lines = doc.splitTextToSize(text, contentWidth);
      const textLineHeight = 5; // Fixed line height for 10pt font
      lines.forEach((line, index) => {
        doc.text(line, margin, startY + (index * textLineHeight));
      });
      return startY + (lines.length * textLineHeight);
    };
    
    const paragraphGap = 6; // Consistent gap between paragraphs
    
    const reg1 = '1. All the rules, regulations will bind you and TekTap Consulting Private Limited policies, whether in existence now or as they may be supplemented and modified from time to time.';
    yPos = renderParagraph(reg1, yPos) + paragraphGap;
    
    const reg2 = '2. During the term of your employment with TekTap Consulting Private Limited and after the termination of your employment for any reason, you shall not, directly or indirectly: (i) induce or attempt to induce any employee or contractor to terminate any employment or any contractual relationship with TekTap Consulting Private Limited (ii) interfere with or disrupt TekTap Consulting Private Limited relationship with any employee or contractor, or (iii) solicit, entice, take away, employ, contract with or engage any employee or contractor employed or engaged by TekTap Consulting Private Limited or with whom TekTap Consulting Private Limited has a contractual relationship. During the term of your employment with TekTap Consulting Private Limited and after the termination of your employment for any reason, you shall not contact, solicit or attempt to solicit, contract with or for, or provide services to, on your behalf or behalf of any other person or entity, any Customer to offer or provide any service that is competitive with the business of TekTap Consulting Private Limited. The term Customer as used herein shall mean any private-sector customer of TekTap Consulting Private Limited, or any program, division or office of any government for which TekTap Consulting Private Limited had performed, or in part, any contract; any successor in interest, subsidiary, affiliate, agency or a particular division of a customer of TekTap Consulting Private Limited; and any private-sector person or entity, or any program, division or office of any government, to which TekTap Consulting Private Limited had presented a proposal, or with which TekTap Consulting Private Limited was otherwise actively involved in negotiations or preparations to be a customer during the term of your employment with TekTap Consulting Private Limited. As used herein, the "business of TekTap Consulting Private Limited" shall mean providing Information Technology Strategy and Solution services.';
    yPos = renderParagraph(reg2, yPos) + paragraphGap;
    
    const reg3 = '3. Upon termination/resignation of employment, you will immediately surrender all material belonging to TekTap Consulting Private Limited or created during your employment with TekTap Private Limited. This will include but not be limited to all notes, correspondence, documents, books and periodicals, client lists, records, databases, computer and storage media, programs, and related data of any kind in any way associated with TekTap Consulting Private Limited or any of its affiliates or the work conducted during the period of your employment. TekTap Consulting Private Limited shall not relieve you from employment until you pay back to TekTap Consulting Private Limited all obligations, if any, which you owe to TekTap Consulting Private Limited in the form of cash or property.';
    yPos = renderParagraph(reg3, yPos) + paragraphGap;
    
    const reg4 = '4. During the employment with TekTap Consulting Private Limited, you were not supposed to do dual employment. If so, your job will be terminated without any prior notice.';
    yPos = renderParagraph(reg4, yPos) + paragraphGap;
    
    // ==================== Continue with More Terms on same page ====================
    const term5 = '5. You shall not disclose or discuss your remuneration details with anyone within or outside of TekTap Consulting Private Limited. Any violation of this clause will lead to disciplinary action, including termination of services with immediate effect.';
    yPos = renderParagraph(term5, yPos) + paragraphGap;
    
    const term6 = "6. Notwithstanding anything to the contrary herein, misconduct on your part (such misconduct to be determined in the sole discretion of the Board of Directors), such as dishonesty, non-performance of your duties hereunder, disobedience, misappropriation, theft, fraud, disorderly behaviour, negligence, lack of discipline, absence from duty without permission, or any violation of one or more terms of this appointment, will entitle TekTap Consulting Private Limited, acting in its sole discretion, to terminate your services without any notice or payment in lieu of notice.";
    
    // Check if term6 fits on current page
    const term6Lines = doc.splitTextToSize(term6, contentWidth);
    const term6Height = term6Lines.length * 5;
    if (yPos + term6Height > pageHeight - 40) {
      addFooter();
      doc.addPage();
      yPos = addLogo(10);
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...darkText);
    }
    
    yPos = renderParagraph(term6, yPos) + paragraphGap;
    
    const term7 = "7. Any dispute arising out of this will be subject to Telangana's jurisdiction of the court of laws.";
    yPos = renderParagraph(term7, yPos) + 10;
    
    addSignature(yPos);
    addFooter();
    
    // Save PDF
    const fileName = `Offer_Letter_${offerFormData.fullName.replace(/\s+/g, '_')}_${offerFormData.letterDate}.pdf`;
    doc.save(fileName);
  };

  const generatePDF = async () => {
    if (!breakdown) return;

    const doc = new jsPDF();
    
    // Load Roboto font
    const fontLoaded = await addRobotoFont(doc);
    const fontFamily = fontLoaded ? 'Roboto' : 'helvetica';
    
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Colors
    const primaryColor = [30, 58, 138]; // #1E3A8A
    const headerBg = [241, 245, 249]; // #F1F5F9
    const textDark = [30, 41, 59]; // #1E293B
    const textLight = [100, 116, 139]; // #64748B
    const accentGreen = [34, 197, 94]; // #22C55E
    
    let yPos = 20;
    
    // Header
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(fontFamily, 'bold');
    doc.text('CTC Breakdown', pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(fontFamily, 'normal');
    doc.text(`Annual CTC: ${formatCurrency(Number(annualCTC))}`, pageWidth / 2, 30, { align: 'center' });
    
    yPos = 55;
    
    // Summary Section
    doc.setTextColor(...textDark);
    doc.setFontSize(14);
    doc.setFont(fontFamily, 'bold');
    doc.text('Summary', 20, yPos);
    yPos += 10;
    
    // Summary boxes
    const summaryData = [
      { label: 'Gross Salary', value: formatCurrency(breakdown.monthly.totalGross), annual: formatCurrency(breakdown.annual.totalGross) },
      { label: 'Benefits', value: formatCurrency(breakdown.monthly.totalBenefits), annual: formatCurrency(breakdown.annual.totalBenefits) },
      { label: 'Deductions', value: formatCurrency(breakdown.monthly.employeePF + 200), annual: formatCurrency(breakdown.annual.employeePF + 2400) },
      { label: 'In-Hand', value: formatCurrency(breakdown.monthly.inHand), annual: formatCurrency(breakdown.annual.grossSalary) },
    ];
    
    const boxWidth = 42;
    const boxHeight = 25;
    const startX = 20;
    
    summaryData.forEach((item, index) => {
      const x = startX + (index * (boxWidth + 5));
      doc.setFillColor(...headerBg);
      doc.roundedRect(x, yPos, boxWidth, boxHeight, 3, 3, 'F');
      
      doc.setTextColor(...textLight);
      doc.setFontSize(8);
      doc.setFont(fontFamily, 'normal');
      doc.text(item.label, x + boxWidth / 2, yPos + 8, { align: 'center' });
      
      doc.setTextColor(...textDark);
      doc.setFontSize(10);
      doc.setFont(fontFamily, 'bold');
      doc.text(item.value, x + boxWidth / 2, yPos + 16, { align: 'center' });
      
      doc.setTextColor(...textLight);
      doc.setFontSize(7);
      doc.setFont(fontFamily, 'normal');
      doc.text('/month', x + boxWidth / 2, yPos + 22, { align: 'center' });
    });
    
    yPos += 40;
    
    // Earnings Section
    doc.setTextColor(...textDark);
    doc.setFontSize(14);
    doc.setFont(fontFamily, 'bold');
    doc.text('Earnings', 20, yPos);
    yPos += 8;
    
    // Table Header
    doc.setFillColor(...primaryColor);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont(fontFamily, 'bold');
    doc.text('Component', 25, yPos + 5.5);
    doc.text('Monthly', 110, yPos + 5.5);
    doc.text('Annual', 150, yPos + 5.5);
    yPos += 10;
    
    // Earnings rows
    const earningsData = [
      { component: 'Basic Salary', monthly: breakdown.monthly.basic, annual: breakdown.annual.basic },
      { component: 'House Rent Allowance (HRA)', monthly: breakdown.monthly.hra, annual: breakdown.annual.hra },
      { component: 'Special Allowance', monthly: breakdown.monthly.specialAllowance, annual: breakdown.annual.specialAllowance },
      { component: 'Conveyance Allowance', monthly: breakdown.monthly.conveyance, annual: breakdown.annual.conveyance },
      { component: 'Fixed Salary', monthly: breakdown.monthly.grossSalary, annual: breakdown.annual.grossSalary, isTotal: true },
      { component: 'Employee PF Contribution', monthly: breakdown.monthly.employeePF, annual: breakdown.annual.employeePF },
      { component: 'Gross Salary', monthly: breakdown.monthly.totalGross, annual: breakdown.annual.totalGross, isTotal: true },
    ];
    
    earningsData.forEach((row, index) => {
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, yPos - 1, pageWidth - 40, 7, 'F');
      }
      
      if (row.isTotal) {
        doc.setFillColor(...headerBg);
        doc.rect(20, yPos - 1, pageWidth - 40, 7, 'F');
        doc.setFont(fontFamily, 'bold');
      } else {
        doc.setFont(fontFamily, 'normal');
      }
      
      doc.setTextColor(...textDark);
      doc.setFontSize(9);
      doc.text(row.component, 25, yPos + 4);
      doc.text(formatCurrency(row.monthly), 110, yPos + 4);
      doc.text(formatCurrency(row.annual), 150, yPos + 4);
      yPos += 7;
    });
    
    yPos += 10;
    
    // Benefits Section
    doc.setTextColor(...textDark);
    doc.setFontSize(14);
    doc.setFont(fontFamily, 'bold');
    doc.text('Benefits', 20, yPos);
    yPos += 8;
    
    // Table Header
    doc.setFillColor(...primaryColor);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont(fontFamily, 'bold');
    doc.text('Component', 25, yPos + 5.5);
    doc.text('Monthly', 110, yPos + 5.5);
    doc.text('Annual', 150, yPos + 5.5);
    yPos += 10;
    
    // Benefits rows
    const benefitsData = [
      { component: 'Employer PF Contribution', monthly: breakdown.monthly.employerPF, annual: breakdown.annual.employerPF },
      { component: 'Gratuity', monthly: breakdown.monthly.gratuity, annual: breakdown.annual.gratuity },
      { component: 'Medical Insurance Premium', monthly: breakdown.monthly.medicalInsurance, annual: breakdown.annual.medicalInsurance },
      { component: 'Total CTC', monthly: Math.round(Number(annualCTC) / 12), annual: Number(annualCTC), isTotal: true },
    ];
    
    benefitsData.forEach((row, index) => {
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, yPos - 1, pageWidth - 40, 7, 'F');
      }
      
      if (row.isTotal) {
        doc.setFillColor(...headerBg);
        doc.rect(20, yPos - 1, pageWidth - 40, 7, 'F');
        doc.setFont(fontFamily, 'bold');
      } else {
        doc.setFont(fontFamily, 'normal');
      }
      
      doc.setTextColor(...textDark);
      doc.setFontSize(9);
      doc.text(row.component, 25, yPos + 4);
      doc.text(formatCurrency(row.monthly), 110, yPos + 4);
      doc.text(formatCurrency(row.annual), 150, yPos + 4);
      yPos += 7;
    });
    
    yPos += 15;
    
    // In-Hand Salary Highlight
    doc.setFillColor(...accentGreen);
    doc.roundedRect(20, yPos, pageWidth - 40, 20, 4, 4, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont(fontFamily, 'normal');
    doc.text('Monthly In-Hand Salary', 30, yPos + 9);
    
    doc.setFontSize(14);
    doc.setFont(fontFamily, 'bold');
    doc.text(formatCurrency(breakdown.monthly.inHand), pageWidth - 30, yPos + 12, { align: 'right' });
    
    yPos += 30;
    
    // Footer note
    doc.setTextColor(...textLight);
    doc.setFontSize(8);
    doc.setFont(fontFamily, 'normal');
    doc.text('* ₹200 will be deducted as Professional Tax monthly', 20, yPos);
    
    yPos += 15;
    
    // Generated timestamp
    doc.setFontSize(7);
    doc.setFont(fontFamily, 'normal');
    const now = new Date();
    doc.text(`Generated on: ${now.toLocaleDateString('en-IN')} at ${now.toLocaleTimeString('en-IN')}`, 20, yPos);
    doc.text('TEKTAP Consulting Private Limited', pageWidth - 20, yPos, { align: 'right' });
    
    // Save PDF
    doc.save(`CTC_Breakdown_${formatCurrency(Number(annualCTC)).replace(/[₹,]/g, '')}.pdf`);
  };

  return (
    <div className="ctc-calculator">
      {/* Page Header with Create Offer Letter Button */}
      <div className="ctc-page-header">
        <button className="create-offer-btn" onClick={handleCreateOfferLetter}>
          <PlusIcon />
          Create Offer Letter
        </button>
      </div>

      {/* Offer Letter Modal */}
      {showOfferModal && (
        <div className="offer-modal-overlay" onClick={handleCloseOfferModal}>
          <div className="offer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="offer-modal-header">
              <h2>Create Offer Letter</h2>
              <button className="modal-close-btn" onClick={handleCloseOfferModal}>
                <CloseIcon />
              </button>
            </div>
            <form className="offer-form" onSubmit={handleSubmitOfferForm}>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={offerFormData.fullName}
                  onChange={handleOfferFormChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="emailId">Email ID</label>
                <input
                  type="email"
                  id="emailId"
                  name="emailId"
                  value={offerFormData.emailId}
                  onChange={handleOfferFormChange}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="letterDate">Letter Generation Date</label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      id="letterDate"
                      name="letterDate"
                      value={offerFormData.letterDate}
                      onChange={handleOfferFormChange}
                      required
                    />
                    <CalendarIcon />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="dateOfJoining">Date of Joining</label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      id="dateOfJoining"
                      name="dateOfJoining"
                      value={offerFormData.dateOfJoining}
                      onChange={handleOfferFormChange}
                      required
                    />
                    <CalendarIcon />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="designation">Designation</label>
                <select
                  id="designation"
                  name="designation"
                  value={offerFormData.designation}
                  onChange={handleOfferFormChange}
                  required
                >
                  <option value="">Select Designation</option>
                  <option value="Software Engineer">Software Engineer</option>
                  <option value="Software Test Engineer">Software Test Engineer</option>
                  <option value="Senior Software Test Engineer">Senior Software Test Engineer</option>
                  <option value="Test Lead">Test Lead</option>
                </select>
              </div>
              {breakdown && (
                <div className="ctc-preview">
                  <span className="ctc-preview-label">Annual CTC:</span>
                  <span className="ctc-preview-value">{formatCurrency(Number(annualCTC))}</span>
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={handleCloseOfferModal}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={!breakdown}>
                  Generate Offer Letter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Top Row - Input and Summary Cards */}
      <div className="ctc-top-row">
        {/* Input Section */}
        <div className="input-card">
          <div className="input-header">
            <CalculatorIcon />
            <h2>Enter Annual CTC</h2>
          </div>
          <div className="input-group">
            <div className="input-wrapper">
              <span className="input-prefix">₹</span>
              <input
                type="text"
                value={annualCTC ? Number(annualCTC).toLocaleString('en-IN') : ''}
                onChange={handleCTCChange}
                placeholder="Enter annual CTC"
                className="ctc-input"
              />
            </div>
            <button className="reset-btn" onClick={handleReset}>
              <ResetIcon />
              Reset
            </button>
          </div>
          <div className="input-footer">
            <p className="input-hint">Enter your annual Cost to Company (CTC) to see the salary breakdown</p>
            {breakdown && (
              <button className="download-link" onClick={generatePDF}>
                <DownloadIcon />
                Download PDF
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {breakdown && (
          <div className="summary-cards">
            <div className="summary-card gross">
              <span className="summary-label">Gross Salary</span>
              <span className="summary-value">{formatCurrency(breakdown.monthly.totalGross)}</span>
              <span className="summary-monthly">{formatCurrency(breakdown.annual.totalGross)}/year</span>
            </div>
            <div className="summary-card benefits">
              <span className="summary-label">Benefits</span>
              <span className="summary-value">{formatCurrency(breakdown.monthly.totalBenefits)}</span>
              <span className="summary-monthly">{formatCurrency(breakdown.annual.totalBenefits)}/year</span>
              <div className="benefits-tooltip">
                <div className="tooltip-row">
                  <span>Employer PF</span>
                  <span>{formatCurrency(breakdown.monthly.employerPF)}</span>
                </div>
                <div className="tooltip-row">
                  <span>Gratuity</span>
                  <span>{formatCurrency(breakdown.monthly.gratuity)}</span>
                </div>
                <div className="tooltip-row">
                  <span>Medical Insurance</span>
                  <span>{formatCurrency(breakdown.monthly.medicalInsurance)}</span>
                </div>
                <div className="tooltip-row total">
                  <span>Total</span>
                  <span>{formatCurrency(breakdown.monthly.totalBenefits)}</span>
                </div>
              </div>
            </div>
            <div className="summary-card deductions">
              <span className="summary-label">Deductions*</span>
              <span className="summary-value">{formatCurrency(breakdown.monthly.employeePF + 200)}</span>
              <span className="summary-monthly">{formatCurrency(breakdown.annual.employeePF + 2400)}/year</span>
              <div className="deductions-tooltip">
                <div className="tooltip-row">
                  <span>Employee PF</span>
                  <span>{formatCurrency(breakdown.monthly.employeePF)}</span>
                </div>
                <div className="tooltip-row">
                  <span>Professional Tax</span>
                  <span>{formatCurrency(200)}</span>
                </div>
                <div className="tooltip-row total">
                  <span>Total</span>
                  <span>{formatCurrency(breakdown.monthly.employeePF + 200)}</span>
                </div>
              </div>
            </div>
            <div className="summary-card net">
              <span className="summary-label">In-Hand</span>
              <span className="summary-value">{formatCurrency(breakdown.monthly.inHand)}</span>
              <span className="summary-monthly">{formatCurrency(breakdown.annual.grossSalary)}/year</span>
            </div>
          </div>
        )}
      </div>

      {/* Results Section */}
      {breakdown && (
        <div className="ctc-results">

          {/* Detailed Breakdown */}
          <div className="breakdown-section">
            <div className="breakdown-card">
              <h3>Earnings</h3>
              <div className="breakdown-table">
                <div className="breakdown-header">
                  <span>Component</span>
                  <span>Monthly</span>
                  <span>Annual</span>
                </div>
                <div className="breakdown-row">
                  <span>Basic Salary</span>
                  <span>{formatCurrency(breakdown.monthly.basic)}</span>
                  <span>{formatCurrency(breakdown.annual.basic)}</span>
                </div>
                <div className="breakdown-row">
                  <span>House Rent Allowance (HRA)</span>
                  <span>{formatCurrency(breakdown.monthly.hra)}</span>
                  <span>{formatCurrency(breakdown.annual.hra)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Special Allowance</span>
                  <span>{formatCurrency(breakdown.monthly.specialAllowance)}</span>
                  <span>{formatCurrency(breakdown.annual.specialAllowance)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Conveyance Allowance</span>
                  <span>{formatCurrency(breakdown.monthly.conveyance)}</span>
                  <span>{formatCurrency(breakdown.annual.conveyance)}</span>
                </div>
                <div className="breakdown-row total">
                  <span>Fixed Salary</span>
                  <span>{formatCurrency(breakdown.monthly.grossSalary)}</span>
                  <span>{formatCurrency(breakdown.annual.grossSalary)}</span>
                </div>
                <div className="breakdown-row pf-row">
                  <span>Employee PF Contribution</span>
                  <span>{formatCurrency(breakdown.monthly.employeePF)}</span>
                  <span>{formatCurrency(breakdown.annual.employeePF)}</span>
                </div>
                <div className="breakdown-row total">
                  <span>Gross Salary</span>
                  <span>{formatCurrency(breakdown.monthly.totalGross)}</span>
                  <span>{formatCurrency(breakdown.annual.totalGross)}</span>
                </div>
              </div>
            </div>

            <div className="breakdown-card">
              <h3>Benefits</h3>
              <div className="breakdown-table">
                <div className="breakdown-header">
                  <span>Component</span>
                  <span>Monthly</span>
                  <span>Annual</span>
                </div>
                <div className="breakdown-row">
                  <span>Employer PF Contribution</span>
                  <span>{formatCurrency(breakdown.monthly.employerPF)}</span>
                  <span>{formatCurrency(breakdown.annual.employerPF)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Gratuity</span>
                  <span>{formatCurrency(breakdown.monthly.gratuity)}</span>
                  <span>{formatCurrency(breakdown.annual.gratuity)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Medical Insurance Premium</span>
                  <span>{formatCurrency(breakdown.monthly.medicalInsurance)}</span>
                  <span>{formatCurrency(breakdown.annual.medicalInsurance)}</span>
                </div>
                <div className="breakdown-row total">
                  <span>Total CTC</span>
                  <span>{formatCurrency(Math.round(Number(annualCTC) / 12))}</span>
                  <span>{formatCurrency(Number(annualCTC))}</span>
                </div>
              </div>
              <p className="ctc-note">* ₹200 will be deducted as Professional Tax monthly</p>
            </div>

            {/* Net Salary Highlight */}
            <div className="net-salary-card">
              <div className="net-salary-icon">
                <RupeeIcon />
              </div>
              <div className="net-salary-info">
                <span className="net-salary-label">Monthly In-Hand Salary</span>
                <span className="net-salary-value">{formatCurrency(breakdown.monthly.inHand)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!breakdown && (
        <div className="empty-state">
          <div className="empty-icon">
            <CalculatorIcon />
          </div>
          <h3>Enter CTC to Calculate</h3>
          <p>Enter your annual CTC above to see a detailed salary breakdown including earnings, deductions, and in-hand salary.</p>
        </div>
      )}
    </div>
  );
};

export default CTCCalculator;

